import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ListChecks, Building2, Users, ArrowUpRight, ShieldCheck, Cpu, Banknote,
} from "lucide-react";
import { getDashboard, approveJob } from "@/api/entities";
import { Panel, Label, Stat, Heading, Loading, Pill, HudButton, accent } from "@/components/hud";
import ReactorGauge from "@/components/ReactorGauge";
import { useOperator } from "@/context/OperatorContext";
import { fmtMoney, fmtMoneyShort, timeAgo } from "@/lib/format";
import { toast } from "sonner";

const STATE_TONE = { queued: "amber", approved: "cyan", running: "violet", review: "magenta", done: "mint", rejected: "amber" };

export default function CommandDeck() {
  const { canWrite } = useOperator();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await getDashboard());
    } catch {
      toast.error("Telemetry link failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await approveJob(id, "BASHAM");
      toast.success("Job approved — ready to run");
      load();
    } catch {
      toast.error("Approval requires operator sign-in");
    }
  };

  if (loading) return <Loading label="Booting command deck…" />;
  if (!data) return null;

  const t = data.treasury;
  const c = data.counts;
  const online = t.stripe_configured && t.settled_cents > 0;

  return (
    <div className="space-y-6 yb-rise" data-testid="command-deck-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Label>Command Deck · live</Label>
          <h1 className="font-display text-[26px] sm:text-[34px] mt-1.5" style={{ color: "var(--yb-text)" }}>
            Mission Control
          </h1>
        </div>
        <Pill tone="mint">All systems nominal</Pill>
      </div>

      {/* top grid: reactor + key stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="lg:col-span-1 flex flex-col items-center justify-center" scan>
          <Label className="self-start mb-2">Treasury Core</Label>
          <ReactorGauge cents={t.settled_cents} currency={t.currency} online={online} />
          <Link to="/treasury" className="mt-3 font-head text-[12px] inline-flex items-center gap-1"
                style={{ color: "var(--yb-cyan)" }}>
            Open treasury <ArrowUpRight size={13} />
          </Link>
        </Panel>

        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <Panel><Stat label="Settled (lifetime)" value={fmtMoney(t.settled_cents, { currency: t.currency })} sub={t.stripe_configured ? "real Stripe revenue" : "connect Stripe to populate"} /></Panel>
          <Panel><Stat label="Paid · last 30d" value={fmtMoney(t.paid_30d_cents, { currency: t.currency })} accent="mint" sub="settled in window" /></Panel>
          <Panel>
            <Stat label="Pipeline (open)" value={fmtMoney(t.pipeline_cents, { currency: t.currency })} accent="amber" sub="projected · not settled" />
            <div className="mt-2 font-mono text-[10px]" style={{ color: "var(--yb-text-muted)" }}>
              ▲ tracked separately — never added to settled
            </div>
          </Panel>
          <Panel><Stat label="Last sync" value={timeAgo(t.last_synced)} accent="violet" mono sub={t.stripe_configured ? "treasury synced" : "stripe offline"} /></Panel>
        </div>
      </div>

      {/* counts row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniStat icon={Building2} label="Active clients" value={`${c.clients_active}/${c.clients_total}`} tone="cyan" to="/clients" />
        <MiniStat icon={ListChecks} label="Awaiting approval" value={c.jobs_awaiting_approval} tone="amber" to="/jobs" />
        <MiniStat icon={Activity} label="In review" value={c.jobs_in_review} tone="magenta" to="/jobs" />
        <MiniStat icon={Users} label="Crew engaged" value={`${c.agents_engaged}/${c.agents_total}`} tone="mint" to="/crew" />
      </div>

      {/* approval queue + crew snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel className="lg:col-span-2">
          <Heading title="Approval Queue" kicker="Human-in-the-loop" accent="amber"
            right={<Link to="/jobs" className="font-head text-[12px]" style={{ color: "var(--yb-cyan)" }}>View all →</Link>} />
          {data.approval_queue.length === 0 ? (
            <div className="font-mono text-[12px] py-6 text-center" style={{ color: "var(--yb-text-muted)" }}>
              Queue clear. No agent work pending review.
            </div>
          ) : (
            <ul className="space-y-2">
              {data.approval_queue.map((j) => (
                <li key={j.id} className="flex items-center gap-3 p-3 rounded"
                    style={{ border: "1px solid var(--yb-hairline)", background: "var(--yb-panel-2)" }}>
                  <span className="dot" style={{ color: accent(STATE_TONE[j.state]).c }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] truncate" style={{ color: "var(--yb-text)" }}>{j.title}</div>
                    <div className="font-mono text-[11px] truncate" style={{ color: "var(--yb-text-muted)" }}>
                      {j.agent_name} · {j.client_name || "internal"}
                    </div>
                  </div>
                  <span className="pill" style={{ color: accent(STATE_TONE[j.state]).c }}>{j.state}</span>
                  {j.state === "queued" && canWrite && (
                    <HudButton tone="mint" onClick={() => approve(j.id)} className="h-8 px-3">Approve</HudButton>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel>
          <Heading title="Crew" kicker="4 divisions" accent="violet" />
          <ul className="space-y-2">
            {data.agents.slice(0, 6).map((a) => (
              <li key={a.id} className="flex items-center gap-2.5 text-[12px]">
                <Cpu size={14} style={{ color: accent(a.color).c }} />
                <span className="flex-1 truncate" style={{ color: "var(--yb-text-dim)" }}>{a.name}</span>
                <span className="font-mono text-[10px] uppercase"
                      style={{ color: a.status === "engaged" ? "var(--yb-mint)" : "var(--yb-text-muted)" }}>
                  {a.status}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/crew" className="mt-3 inline-block font-head text-[12px]" style={{ color: "var(--yb-cyan)" }}>
            Full roster →
          </Link>
        </Panel>
      </div>

      <Panel className="flex items-center gap-3" style={{ borderColor: "var(--yb-hairline)" }}>
        <ShieldCheck size={18} style={{ color: "var(--yb-mint)" }} />
        <p className="text-[12px]" style={{ color: "var(--yb-text-muted)" }}>
          <span style={{ color: "var(--yb-text-dim)" }}>Guardrail active.</span> No funds move autonomously and
          no agent job runs without explicit human approval. Money shown is real Stripe-settled value only.
        </p>
      </Panel>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, tone, to }) {
  const { c } = accent(tone);
  return (
    <Panel as={Link} to={to} className="flex items-center gap-3 hover:bg-white/[0.02] transition-colors">
      <span className="w-9 h-9 rounded flex items-center justify-center shrink-0"
            style={{ border: `1px solid ${c}`, color: c }}>
        <Icon size={16} />
      </span>
      <div>
        <div className="font-mono text-[20px] leading-none" style={{ color: c }}>{value}</div>
        <div className="font-mono text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--yb-text-muted)" }}>{label}</div>
      </div>
    </Panel>
  );
}
